import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    isEqual as _isEqual,
    cloneDeep as _cloneDeep
} from 'lodash';
import { Controlled as CodeMirror } from 'react-codemirror2';

import { makeStyles, useTheme } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import Checkbox from '@mui/material/Checkbox';

import Validator, { useValidate, checkValidity } from '../../../components/validator';

import SystemService from '../../../data/system-service';
import Auth from '../../../data/auth-service';
import DataContext from '../../../data/data-context';

// const FORMAT_STRINGS = ['$proto$', '$src_ip$', '$src_port$', '$dst_ip$', '$dst_port$', '$sensor_uuid$', '$sensor_ipv4$', '$sensor_ipv6$', '$sensor_ip$',
//     '$sensor_name$', '$sensor_fqdn$', '$timestamp$', '$timestamp_epoch_ms$', '$timestamp_epoch_fract$', '$severity$',
//     '$severity_inv$', '$signature$', '$signature_id$', '$cmc_name$', '$cmc_fqdn$', '$cmc_ip$', '$nic$', '$category$', '$action$',
//     '$alert_group$', '$signature_gid$', '$signature_rev$', '$sys_version$'];

const useStyles = makeStyles(theme => ({
    fullWidth: {
        width: '100%',
    },
    timezone: {
        width: '120px',
    },
    hideFileInput: {
        display: 'none',
    },
    buttonRight: {
        float: 'right',
        marginLeft: theme.spacing(2),
    },
    certBox: {
        marginTop: theme.spacing(2),
        padding: theme.spacing(2),
        border: `1px solid ${theme.palette.primary.main}`,
        background: theme.styled.paper.background,
    },
    codemirrorBox: {
        display: 'block',
        width: '100%',
    },
    codeContainer: {
        height: '120px',
        marginBottom: '-8px',
        '& .react-codemirror2': {
            height: '100%',
        },
        '& .CodeMirror': {
            height: '100%',
        }
    },
}));

const Audit = (props) => {
    const classes = useStyles();
    const theme = useTheme();
    const navigate = useNavigate();
    const { systemSettings, updateSystemSettings } = useContext(DataContext);
    const [systemConstants, setSystemConstants] = useState({
        facilities: [],
        severities: [],
        timezones: [],
    });
    const [auditData, setAuditData] = useState({
        enabled: false,
        export_configs: {
            default: {
                dest: {
                    host: 'localhost',
                    port: '514',
                    proto: 'UDP',
                },
                mark: {
                    enable: false,
                    timeout: 11,
                },
                syslog: {
                    dst_tz: 'UTC',
                    facility: 19,
                    format: '$cmc_name$ CEF:0|Bricata|Bricata|$sys_version$|$signature_id$|$signature$|$severity_inv$|src=$src_ip$ rt=$timestamp_epoch_ms$ cs1=$cmc_ip$ cs1Label=CMC-IP cs2=$cmc_name$ cs2Label=BricataCmcHostname cs3=$time$ cs3Label=Audit-time cs4=$source$ cs4Label=Source cs5=$host$ cs5Label=Host cs6=$user$ cs6Label=User cs7=$action$ cs7Label=Action cs8=$subject$ cs8Label=Subject cs9=$result$ cs9Label=Result',
                    frame_separator: '',
                    rfc: 'new',
                    severity: 1,
                },
                tls_cert: {
                    contents: '',
                    enable: false,
                    file_name: '',
                },
                tls_fips: false,
            }
        }
    });
    const [original, setOriginal] = useState();
    const [changes, setChanges] = useState(false);
    const auditForm = useValidate({});
    const [, setFormatRef] = useState(null);
    const auditExportPerms = Auth.permissions.get('AuditExport');

    useEffect(() => {
        SystemService.getConstants().then((res) => {
            setSystemConstants(res);
        });
    }, []);

    useEffect(() => {
        if (systemSettings && systemSettings.audit_export) {
            setAuditData(_cloneDeep(systemSettings.audit_export));
            setOriginal(_cloneDeep(systemSettings.audit_export));
        }
    }, [systemSettings]);

    useEffect(() => {
        if (systemSettings && systemSettings.audit_export) {
            setChanges(_isEqual(auditData, original));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auditData]);

    const handleEnableAudit = (e) => {
        setAuditData({
            ...auditData,
            enabled: e.currentTarget.checked,
        });
    };

    const handleHost = (e, validID, valid) => {
        auditForm.set({ ...auditForm.get(), [validID]: valid });

        const a = { ...auditData };
        a.export_configs.default.dest.host = e.target.value;
        setAuditData(a);
    };

    const handlePort = (e, validID, valid) => {
        auditForm.set({ ...auditForm.get(), [validID]: valid });

        const a = { ...auditData };
        a.export_configs.default.dest.port = e.target.value;
        setAuditData(a);
    };

    const handleProtocol = (e, component) => {
        const a = { ...auditData };
        a.export_configs.default.dest.proto = component.props.value;
        setAuditData(a);
    };

    const handleMark = (e) => {
        const a = { ...auditData };
        a.export_configs.default.mark.enable = e.currentTarget.checked;
        setAuditData(a);
    };

    const handleMarkTimeout = (e, validID, valid) => {
        auditForm.set({ ...auditForm.get(), [validID]: valid });

        const a = { ...auditData };
        a.export_configs.default.mark.timeout = parseInt(e.target.value);
        setAuditData(a);
    };

    const handleTimeZone = (e, component) => {
        const a = { ...auditData };
        a.export_configs.default.syslog.dst_tz = component.props.value;
        setAuditData(a);
    };

    const handleFacility = (e, component) => {
        const a = { ...auditData };
        a.export_configs.default.syslog.facility = component.props.value;
        setAuditData(a);
    };

    const handleFrameSeparator = (e) => {
        const a = { ...auditData };
        a.export_configs.default.syslog.frame_separator = e.currentTarget.value;
        setAuditData(a);
    };

    const handleRFC = (e) => {
        const a = { ...auditData };
        a.export_configs.default.syslog.rfc = e.currentTarget.value;
        setAuditData(a);
    };

    const handleSeverity = (e, component) => {
        const a = { ...auditData };
        a.export_configs.default.syslog.severity = component.props.value;
        setAuditData(a);
    };

    const handleCACert = (e) => {
        const a = { ...auditData };
        a.export_configs.default.tls_cert.enable = e.currentTarget.checked;
        if (!e.currentTarget.checked) {
            a.export_configs.default.tls_cert.contents = '';
            a.export_configs.default.tls_cert.file_name = '';
        }
        setAuditData(a);
    };

    const fileHandler = (file) => {
        const reader = new FileReader();
        return new Promise((resolve, reject) => {
            reader.onload = event => resolve(event.target.result);
            reader.onerror = error => reject(error);
            reader.readAsText(file);
        });
    };

    const handleTLSContents = (e) => {
        fileHandler(e.target.files[0]).then(content => {
            SystemService.auditExportsCertAttributes(content).then((res) => {
                const a = { ...auditData };
                a.export_configs.default.tls_cert.contents = content;
                a.export_configs.default.tls_cert.file_name = `${res.subject} (${res.enddate})`;
                setAuditData(a);
                toast.success('Certificate uploaded successfully');
            }, (err) => {
                toast.error(err);
            });
        }).catch(err => {
            toast.error(err);
        });
    };

    const handleFIPS = (e) => {
        const a = { ...auditData };
        a.export_configs.default.tls_fips = e.currentTarget.checked;
        setAuditData(a);
    };

    const handleUpdateSyslogFormat = (value) => {
        const a = { ...auditData };
        a.export_configs.default.syslog.format = value;
        setAuditData(a);
    };

    // const handleAddFormatString = (e) => {
    //     const a = { ...auditData };
    //     const cp = formatRef.getCursor().ch;

    //     a.export_configs.default.syslog.format = [
    //         a.export_configs.default.syslog.format.slice(0, cp),
    //         e.currentTarget.name,
    //         a.export_configs.default.syslog.format.slice(cp)
    //     ].join('');

    //     setAuditData(a);
    // };

    const handleSave = () => {
        // both API and context will merge on update
        // context update will alos toast the user
        updateSystemSettings({ audit_export: auditData });
    };

    const handleCancel = () => {
        navigate('/system/configuration');
    };

    const codemirrorOptions = {
        theme: theme.codeMirror.theme,
        lineWrapping: true,
        readOnly: true,
    };

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Paper elevation={3}>
                    <Box p={5}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    disabled={!auditExportPerms.admin}
                                    label="Enable"
                                    control={
                                        <Checkbox
                                            checked={auditData.enabled}
                                            onChange={handleEnableAudit}
                                        />
                                    }
                                />
                            </Grid>
                            <Grid item container xs={8} spacing={2}>
                                <Grid item xs={6}>
                                    <Box>
                                        <FormControl className={classes.fullWidth}>
                                            <FormLabel>Protocol</FormLabel>
                                            <Select disabled={!auditExportPerms.write || !auditData.enabled} className={classes.auditInput} value={auditData.export_configs.default.dest.proto} onChange={handleProtocol}>
                                                <MenuItem value="UDP">UDP</MenuItem>
                                                <MenuItem value="TCP">TCP</MenuItem>
                                                <MenuItem value="TLS">TLS</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Box>
                                    <Box mt={3}>
                                        <Validator
                                            valueType="host"
                                            errorText="Hostname is invalid"
                                            fullWidth
                                            label="Hostname"
                                            value={auditData.export_configs.default.dest.host}
                                            onChange={handleHost}
                                            disabled={!auditExportPerms.write || !auditData.enabled}
                                        />
                                    </Box>
                                    <Box mt={3}>
                                        <Validator
                                            valueType="port"
                                            errorText="Port is invalid"
                                            fullWidth
                                            label="Port"
                                            value={auditData.export_configs.default.dest.port}
                                            onChange={handlePort}
                                            disabled={!auditExportPerms.write || !auditData.enabled}
                                        />
                                    </Box>
                                    <Box mt={3}>
                                        <FormControl>
                                            <FormLabel>RFC</FormLabel>
                                            <RadioGroup name="rfc" row value={auditData.export_configs.default.syslog.rfc} onChange={handleRFC}>
                                                <FormControlLabel value="new" control={<Radio />} label="5424" disabled={!auditExportPerms.write || !auditData.enabled} />
                                                <FormControlLabel value="old" control={<Radio />} label="3164" disabled={!auditExportPerms.write || !auditData.enabled} />
                                            </RadioGroup>
                                        </FormControl>
                                    </Box>
                                </Grid>
                                <Grid item xs={6}>
                                    <Box>
                                        <FormControl className={classes.fullWidth}>
                                            <FormLabel>Facility</FormLabel>
                                            <Select className={classes.fullWidth} disabled={!auditExportPerms.write || !auditData.enabled} value={auditData.export_configs.default.syslog.facility} onChange={handleFacility}>
                                                {Object.keys(systemConstants.facilities).map((v, i) => {
                                                    return <MenuItem key={'facility-option-' + i} value={systemConstants.facilities[v]}>{v}</MenuItem>;
                                                })}
                                            </Select>
                                        </FormControl>
                                    </Box>
                                    <Box mt={3}>
                                        <FormControl className={classes.fullWidth}>
                                            <FormLabel>Severity</FormLabel>
                                            <Select className={classes.fullWidth} disabled={!auditExportPerms.write || !auditData.enabled} value={auditData.export_configs.default.syslog.severity} onChange={handleSeverity}>
                                                {Object.keys(systemConstants.severities).map((v, i) => {
                                                    return <MenuItem key={'severity-option-' + i} value={systemConstants.severities[v]}>{v}</MenuItem>;
                                                })}
                                            </Select>
                                        </FormControl>
                                    </Box>
                                    <Box mt={3}>
                                        <FormControl className={classes.fullWidth}>
                                            <FormLabel>Destination Time Zone</FormLabel>
                                            <Select className={classes.fullWidth} disabled={!auditExportPerms.write || !auditData.enabled} value={auditData.export_configs.default.syslog.dst_tz} onChange={handleTimeZone}>
                                                {systemConstants.timezones.map((v, i) => {
                                                    return <MenuItem key={'timezone-' + i} value={v.name}>{v.utc_offset} - {v.name}</MenuItem>;
                                                })}
                                            </Select>
                                        </FormControl>
                                    </Box>
                                    <Box mt={3} display={process.env.BRICATA_APPLIANCE_TYPE === 'mc2' ? 'none' : 'block'}>
                                        <RadioGroup row>
                                            <FormControlLabel
                                                label="Mark"
                                                labelPlacement="start"
                                                disabled={!auditExportPerms.write || !auditData.enabled}
                                                control={
                                                    <Checkbox
                                                        checked={auditData.export_configs.default.mark.enable}
                                                        name="mark"
                                                        onChange={handleMark}
                                                    />
                                                }
                                            />
                                            <Box ml={3}>
                                                <FormControlLabel
                                                    label=""
                                                    disabled={!auditData.export_configs.default.mark.enable || !auditData.enabled}
                                                    control={
                                                        <Validator
                                                            valueType="range"
                                                            errorText="Interval timeout must be within 1 and 1440 minutes"
                                                            type="number"
                                                            inputProps={{
                                                                min: 1,
                                                                max: 1440,
                                                            }}
                                                            endAdornment={<InputAdornment position="end">Minutes</InputAdornment>}
                                                            value={auditData.export_configs.default.mark.timeout}
                                                            onChange={handleMarkTimeout}
                                                        />
                                                    }
                                                />
                                            </Box>
                                        </RadioGroup>
                                    </Box>
                                </Grid>
                            </Grid>
                            <Grid item container xs={4}>
                                <Grid item xs={12}>
                                    <Box>
                                        TCP Frame Separator
                                        <RadioGroup
                                            row
                                            value={auditData.export_configs.default.syslog.frame_separator}
                                            onChange={handleFrameSeparator}
                                        >
                                            <FormControlLabel
                                                labelPlacement="start"
                                                label="Frame Length"
                                                value=""
                                                disabled={!auditExportPerms.write || auditData.export_configs.default.dest.proto === 'UDP' || !auditData.enabled}
                                                control={<Radio />}
                                            />
                                            <FormControlLabel
                                                labelPlacement="start"
                                                label="New Line"
                                                value="'\n'"
                                                disabled={!auditExportPerms.write || auditData.export_configs.default.dest.proto === 'UDP' || !auditData.enabled}
                                                control={<Radio />}
                                            />
                                        </RadioGroup>
                                    </Box>
                                    <Box mt={3}>
                                        <FormControlLabel
                                            labelPlacement="start"
                                            label="FIPS"
                                            disabled={!auditExportPerms.write || auditData.export_configs.default.dest.proto !== 'TLS' || !auditData.enabled}
                                            control={
                                                <Checkbox
                                                    checked={auditData.export_configs.default.tls_fips}
                                                    name=""
                                                    onChange={handleFIPS}
                                                />
                                            }
                                        />
                                    </Box>
                                    <Box mt={3}>
                                        <FormControlLabel
                                            labelPlacement="start"
                                            label="CA Cert"
                                            disabled={!auditExportPerms.write || auditData.export_configs.default.dest.proto !== 'TLS' || !auditData.enabled}
                                            control={
                                                <Checkbox
                                                    checked={auditData.export_configs.default.tls_cert.enable}
                                                    name=""
                                                    onChange={handleCACert}
                                                />
                                            }
                                        />
                                    </Box>
                                    <Box mt={3}>
                                        <input
                                            id="contained-button-file"
                                            className={classes.hideFileInput}
                                            type="file"
                                            onChange={handleTLSContents}
                                            disabled={!auditExportPerms.write || !auditData.export_configs.default.tls_cert.enable || !auditData.enabled || auditData.export_configs.default.dest.proto !== 'TLS'}
                                        />
                                        <label htmlFor="contained-button-file">
                                            <Button
                                                aria-label="upload cert"
                                                variant="contained"
                                                color="primary"
                                                component="span"
                                                disabled={!auditExportPerms.write || !auditData.export_configs.default.tls_cert.enable || !auditData.enabled || auditData.export_configs.default.dest.proto !== 'TLS'}
                                            >
                                                Upload Cert
                                            </Button>
                                        </label>
                                    </Box>
                                    <Box className={classes.certBox} display={auditData.export_configs.default.tls_cert.file_name ? 'flex' : 'none'}>
                                        {auditData.export_configs.default.tls_cert.file_name}
                                    </Box>
                                </Grid>
                            </Grid>
                            <Grid item container xs={12}>
                                <Grid item xs={12}>
                                    <Box className={classes.codeContainer} width="100%">
                                        <Typography>Format</Typography>
                                        <CodeMirror
                                            editorDidMount={(e) => { setFormatRef(e) }}
                                            value={auditData.export_configs.default.syslog.format}
                                            options={codemirrorOptions}
                                            onBeforeChange={(c, e, value) => { handleUpdateSyslogFormat(value) }}
                                            lineWrapping={true}
                                        />
                                    </Box>
                                    {/* <Box mt={1}>
                                        <Accordion>
                                            <AccordionSummary>
                                                <Box display="flex" justifyContent="flex-end">
                                                    Format Strings
                                                </Box>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <Box>
                                                    <Typography>The following format strings are recognized. Select an item to insert it into the Format field.</Typography>
                                                    {FORMAT_STRINGS.map((fs, i) => {
                                                        return (
                                                            <Button
                                                                key={fs+'-'+i}
                                                                name={fs}
                                                                color="primary"
                                                                onClick={handleAddFormatString}
                                                                style={{textTransform: 'none'}}
                                                            >{fs}</Button>
                                                        );
                                                    })}
                                                </Box>
                                            </AccordionDetails>
                                        </Accordion>
                                    </Box> */}
                                </Grid>
                            </Grid>
                            <Grid item container xs={12}>
                                <Grid item xs={12}>
                                    <Box mt={4} display="flex" justifyContent="flex-end">
                                        <Box ml={3}><Button variant="contained" color="secondary" aria-label="Cancel" onClick={handleCancel}>Cancel</Button></Box>
                                        {auditExportPerms.write && <Box ml={3}><Button
                                            variant="contained"
                                            color="primary"
                                            aria-label="Save"
                                            onClick={handleSave}
                                            disabled={!(checkValidity(auditForm.get(), true) && !changes)}
                                        >Save</Button></Box>}
                                    </Box>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>
            </Grid>
        </Grid>
    );
};

export default Audit;
